import { Grid, GridItem, Skeleton, VStack, useDisclosure } from '@chakra-ui/react';
import { useQueryWithErrorBoundary, useSupabaseTypedClient } from 'hooks/reactQuery';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import { PreferencesModal } from 'components/Modal/PreferencesModal';
import { useMediaQueries } from 'layout/MediaQueriesProvider';
import { AchievementExperienceReward } from 'types/customEnums';
import {
  User_Challenge_Status_Enum,
  User_Practice_Problem_Status_Enum,
  User_Project_Status_Enum,
} from 'types/databaseEnums';
import { TanstackQueryName } from 'types/frontend';

import ActivityHeatmap from './ActivityHeatmap';
import JourneyPath from './JourneyPath';
import NodeDetailCard from './NodeDetailCard';
import RecentAchievements from './RecentAchievements';
import {
  clearLastSelection,
  getActivityStartDate,
  getLastSelection,
  getManualCompletions,
  getNodeStatesMap,
  saveLastSelection,
  toggleManualCompletion,
} from './dashboardUtils';
import { getJourneyPath, JourneyNode } from './journeyPathConfig';

const JourneyDashboard = () => {
  const { id: userId, userPreferences } = useCurrentUser();
  const supabase = useSupabaseTypedClient();
  const { isSmallScreen } = useMediaQueries();
  const preferencesModal = useDisclosure();
  const navigate = useNavigate();
  const [selectedNode, setSelectedNode] = useState<JourneyNode | null>(null);
  const [selectedNodeIndex, setSelectedNodeIndex] = useState<number>(-1);
  const [selectionRestored, setSelectionRestored] = useState(false);
  const [manualCompletions, setManualCompletions] = useState<Set<string>>(
    () => getManualCompletions(userId)
  );
  const journeyEndRef = useRef<HTMLDivElement>(null);

  const goal = userPreferences?.goal;
  const exp = userPreferences?.experience_level;
  const focus = userPreferences?.focus_area;
  const sections = getJourneyPath(goal, exp, focus);
  const allNodes = sections.flatMap((s) => s.nodes);

  // Restore last selection from localStorage on mount (before any early return)
  useEffect(() => {
    if (selectionRestored || selectedNode) return;
    const saved = getLastSelection(userId);
    if (saved) {
      const node = allNodes.find((n) => n.id === saved.nodeId);
      if (node) {
        setSelectedNode(node);
        setSelectedNodeIndex(saved.globalIndex);
      }
    }
    setSelectionRestored(true);
  }, [selectionRestored, selectedNode, userId, allNodes]);

  const handleNodeSelect = useCallback((node: JourneyNode, globalIndex: number) => {
    setSelectedNode(node);
    setSelectedNodeIndex(globalIndex);
    saveLastSelection(userId, node.id, globalIndex);
  }, [userId]);

  const handleStartClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedNodeIndex(-1);
    clearLastSelection(userId);
  }, [userId]);

  const handleToggleManualCompletion = useCallback((nodeId: string) => {
    const updated = toggleManualCompletion(userId, nodeId);
    setManualCompletions(new Set(updated));
  }, [userId]);

  const handlePreferencesClose = useCallback(() => {
    preferencesModal.onClose();
  }, [preferencesModal]);

  const activityStartDate = getActivityStartDate();

  // Fetch completion data and activity timestamps for the journey
  const { data: journeyData, isPending } = useQueryWithErrorBoundary({
    queryKey: [TanstackQueryName.GetUserCode, userId, 'journey'],
    queryFn: async () => {
      const achievementsValues = Object.values(AchievementExperienceReward);

      const [
        projectsResult,
        challengesResult,
        problemsResult,
        achievementRewardsResult,
        userExperienceResult,
        activityResult,
      ] = await Promise.all([
        // Fetch completed projects — join through project FK to get entity screen_name
        // (user_project.screen_name is the solution title, not the project identifier)
        supabase
          .from('user_project')
          .select('project (screen_name)')
          .eq('user_id', userId)
          .eq('status', User_Project_Status_Enum.Completed),
        // Fetch completed challenges — join through challenge FK to get entity screen_name
        // Note: user_challenge uses status_type column, not status
        supabase
          .from('user_challenge')
          .select('challenge (screen_name)')
          .eq('user_id', userId)
          .eq('status_type', User_Challenge_Status_Enum.Completed),
        // Fetch completed problems — join through practice_problem FK to get entity screen_name
        supabase
          .from('user_practice_problem')
          .select('practice_problem (screen_name)')
          .eq('user_id', userId)
          .eq('status', User_Practice_Problem_Status_Enum.Completed),
        // Achievement definitions from experience_reward table
        supabase
          .from('experience_reward')
          .select('*')
          .in('id', achievementsValues),
        // User's completed achievements from user_experience table
        supabase
          .from('user_experience')
          .select('id, experience, experience_reward_id, timestamp')
          .eq('user_id', userId)
          .in('experience_reward_id', achievementsValues),
        // Activity dates from dedicated view (qualifying events only)
        supabase
          .from('user_activity_dates_view')
          .select('activity_date')
          .eq('user_id', userId)
          .gte('activity_date', activityStartDate)
          .order('activity_date', { ascending: false }),
      ]);

      // Process achievements by matching rewards with user completions
      const userCompletedIds = (userExperienceResult.data || []).map(
        (ue: any) => ue.experience_reward_id
      );
      const achievements = (achievementRewardsResult.data || []).map((a: any) => {
        const userEntry = (userExperienceResult.data || []).find(
          (ue: any) => ue.experience_reward_id === a.id
        );
        return {
          id: a.id,
          name: a.name,
          description: a.description || '',
          type: a.icon || '',
          xp: a.experience,
          is_completed: userCompletedIds.includes(a.id),
          timestamp: userEntry?.timestamp || '',
        };
      });

      return {
        completedProjects: new Set(
          (projectsResult.data || []).map((r: any) => r.project?.screen_name).filter(Boolean) as string[]
        ),
        completedChallenges: new Set(
          (challengesResult.data || []).map((r: any) => r.challenge?.screen_name).filter(Boolean) as string[]
        ),
        completedProblems: new Set(
          (problemsResult.data || []).map((r: any) => r.practice_problem?.screen_name).filter(Boolean) as string[]
        ),
        achievements,
        activityDates: (activityResult.data || []).map((a: any) => a.activity_date as string),
      };
    },
    enabled: Boolean(userId),
    staleTime: 0,
  });

  if (isPending || !journeyData) {
    return (
      <VStack spacing={4} w="100%">
        <Skeleton height="120px" w="100%" borderRadius="xl" />
        <Skeleton height="80px" w="100%" borderRadius="xl" />
        <Skeleton height="300px" w="100%" borderRadius="xl" />
      </VStack>
    );
  }

  const { completedProjects, completedChallenges, completedProblems, achievements, activityDates } = journeyData;

  // Compute per-node completion state:
  // project/challenge/problem → screenName from DB
  // code-frame/prototype/cv → manual completions from localStorage
  const nodeStatesMap = getNodeStatesMap(allNodes, {
    completedProjects,
    completedChallenges,
    completedProblems,
    manualCompletions,
  });
  const completedNodeCount = Array.from(nodeStatesMap.values()).filter((s) => s === 'completed').length;
  const totalCompleted = achievements.filter((a: any) => a.is_completed).length;

  const achievementsBlock = (
    <RecentAchievements
      achievements={achievements}
      totalCompleted={totalCompleted}
      total={achievements.length}
    />
  );

  const handleNavigate = (route: string) => {
    navigate(route);
  };

  const selectedNodeState = selectedNode
    ? nodeStatesMap.get(selectedNode.id) ?? null
    : null;

  const isManuallyCompleted = selectedNode ? manualCompletions.has(selectedNode.id) : false;

  const nodeDetailCard = (
    <NodeDetailCard
      selectedNode={selectedNode}
      nodeState={selectedNodeState}
      onNavigate={handleNavigate}
      isManuallyCompleted={isManuallyCompleted}
      onToggleManualCompletion={handleToggleManualCompletion}
    />
  );

  const journeyPathBlock = (
    <JourneyPath
      goal={goal}
      experienceLevel={exp}
      focusArea={focus}
      nodeStatesMap={nodeStatesMap}
      totalNodes={allNodes.length}
      onCustomize={preferencesModal.onOpen}
      selectedNodeId={selectedNode?.id}
      onNodeSelect={handleNodeSelect}
      onStartClick={handleStartClick}
      bottomRef={journeyEndRef}
    />
  );

  // Mobile: flat single-column layout — card first, then map, activity, achievements
  if (isSmallScreen) {
    return (
      <>
        <PreferencesModal isOpen={preferencesModal.isOpen} onClose={handlePreferencesClose} />
        <VStack spacing={4} w="100%">
          {nodeDetailCard}
          {journeyPathBlock}
          <ActivityHeatmap activityDates={activityDates} />
          {achievementsBlock}
        </VStack>
      </>
    );
  }

  // Desktop: two-column grid layout
  return (
    <>
      <PreferencesModal isOpen={preferencesModal.isOpen} onClose={handlePreferencesClose} />
      <VStack spacing={4} w="100%">
        <Grid templateColumns="1fr 380px" gap={4} w="100%">
          {/* Left column — journey content */}
          <GridItem>
            <VStack spacing={4} w="100%">
              {journeyPathBlock}
            </VStack>
          </GridItem>

          {/* Right column — detail card, activity, achievements */}
          <GridItem>
            <VStack spacing={4} w="100%">
              {nodeDetailCard}
              <ActivityHeatmap activityDates={activityDates} />
              {achievementsBlock}
            </VStack>
          </GridItem>
        </Grid>
      </VStack>
    </>
  );
};

export default JourneyDashboard;
